import { useEffect, useState } from "react";
import { api, type ClientContextResponse, type ClientContext, type ClientLink } from "@/lib/api";
import { toast } from "sonner";

interface UseClientContextResult {
  data: ClientContextResponse | null;
  isLoading: boolean;
  error: string | null;
  /** Refetch from the server. Use after side-effects like adding a link. */
  refetch: () => Promise<void>;
}

interface UseUpdateClientContextOptions {
  aboutClient?: string;
  theirMarket?: string;
  competitors?: string;
  additionalNotes?: string;
}

export function useClientContext(projectId: string | null | undefined): UseClientContextResult {
  const [data, setData] = useState<ClientContextResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContext = async () => {
    if (!projectId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const context = await api.getClientContext(projectId);
      setData(context);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load client context";
      setError(message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return { data, isLoading, error, refetch: fetchContext };
}

export function useUpdateClientContext(projectId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateContext = async (
    options: UseUpdateClientContextOptions,
  ): Promise<ClientContext | null> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await api.updateClientContext(projectId, options);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update client context";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { updateContext, loading, error };
}

export function useCreateClientLink(projectId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createLink = async (url: string): Promise<ClientLink | null> => {
    try {
      setLoading(true);
      setError(null);
      const link = await api.createClientLink(projectId, url);
      return link;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create link";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createLink, loading, error };
}

export function useDeleteClientLink() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteLink = async (linkId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteClientLink(linkId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete link";
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { deleteLink, loading, error };
}
